/* eslint-disable no-unused-vars */
import React from 'react';
import {
    Button
} from 'shared/components/ui/button';
import {
    Label
} from 'shared/components/ui/label';
import {
    AlertCircle, Plus, Trash2
} from 'lucide-react';

/**
 * HooksAdvanced — opcode-level `hooks` block editor.
 *
 * Schema (OPCODE-SCHEMA.md §14.3, Future-slot-V1):
 *   opcode.hooks = { pre_run?: [hook], post_run?: [hook], on_error?: [hook] }
 *
 * Each list holds free-form hook descriptor objects (JSON). V1 stores them but
 * Mind does not dispatch declarative hooks at runtime — UI is flagged
 * "Informational in V1" per A-0012.01 R11.
 *
 * Sits below the Outputs section in OpcodeBuilder.jsx; <details> collapsible
 * matching the destudio step-common pattern. Initial open state mirrors
 * set-count (open if any kind has entries).
 */

const KINDS = [
    { key: 'pre_run', label: 'pre_run', help: 'run before the first step' },
    { key: 'post_run', label: 'post_run', help: 'run after the last step' },
    { key: 'on_error', label: 'on_error', help: 'run when execution errors' }
];

export default function HooksAdvanced({
    hooks, onPatchOpcode
}) {
    const safe = hooks && typeof hooks === 'object' ? hooks : {};
    const setCount = KINDS.reduce(
        (n, k) => n + ((Array.isArray(safe[k.key]) && safe[k.key].length > 0) ? 1 : 0),
        0
    );

    function patchKind(kind, list) {
        const next = { ...safe };
        if (!list || list.length === 0) {
            delete next[kind];
        } else {
            next[kind] = list;
        }
        const hasAny = Object.keys(next).length > 0;
        onPatchOpcode({ hooks: hasAny ? next : undefined });
    }

    return (
        <details
            data-testid="opcode-hooks-advanced"
            className="rounded-md border bg-muted/20 px-3 py-2.5"
            open={setCount > 0}
        >
            <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Hooks ({setCount}/3 lifecycle hooks set)
            </summary>
            <div className="mt-2 mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50/60 px-2.5 py-1.5 text-[11px] text-amber-900">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                    <b>Informational in V1.</b> Mind stores these descriptors but does not dispatch declarative
                    hooks yet (OPCODE-SCHEMA §14.3, Future-slot-V1).
                </span>
            </div>
            <div className="space-y-3">
                {KINDS.map((k) => (
                    <HookList
                        key={k.key}
                        label={k.label}
                        help={`Hook descriptors to ${k.help}.`}
                        value={Array.isArray(safe[k.key]) ? safe[k.key] : []}
                        onChange={(next) => patchKind(k.key, next)}
                    />
                ))}
            </div>
        </details>
    );
}

/**
 * Minimal V1 hook editor: one row per descriptor, each a JSON textarea.
 * Replace with a typed Hook builder when a typed protocol lands in Mind.
 */
function HookList({ label, help, value, onChange }) {
    const [drafts, setDrafts] = React.useState(() => value.map((h) => stringify(h)));

    // Keep local drafts in sync when external value changes (e.g. on reload).
    React.useEffect(() => {
        setDrafts(value.map((h) => stringify(h)));
    }, [value]);

    function commit(idx, str) {
        const next = drafts.slice();
        next[idx] = str;
        setDrafts(next);
        // Try to parse — only emit on valid JSON; leave the draft string until then.
        const parsed = tryParse(str);
        if (parsed.ok) {
            const out = value.slice();
            out[idx] = parsed.value;
            onChange(out);
        }
    }
    function add() {
        const out = value.slice();
        out.push({});
        onChange(out);
        setDrafts([...drafts, '{}']);
    }
    function remove(idx) {
        const out = value.filter((_, i) => i !== idx);
        onChange(out);
        setDrafts(drafts.filter((_, i) => i !== idx));
    }

    return (
        <div className="space-y-1.5">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {label} <span className="ml-1 text-muted-foreground/70">({value.length})</span>
            </Label>
            {help && <p className="text-[10.5px] text-muted-foreground">{help}</p>}
            {drafts.length === 0 ? (
                <div className="rounded-md border border-dashed bg-muted/20 px-3 py-2 text-[11px] text-muted-foreground">
                    No hooks yet.
                </div>
            ) : (
                drafts.map((s, i) => {
                    const valid = tryParse(s).ok;
                    return (
                        <div key={i} className="grid grid-cols-[1fr_auto] items-start gap-2">
                            <textarea
                                aria-label={`${label} hook ${i + 1} JSON`}
                                value={s}
                                onChange={(e) => commit(i, e.target.value)}
                                rows={2}
                                className={
                                    'w-full rounded-md border bg-card px-2 py-1 font-mono text-[12px] ' +
                                    (valid ? '' : 'border-destructive')
                                }
                                placeholder='{ "type": "memory_append" }'
                            />
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => remove(i)}
                                className="h-8 px-2 text-muted-foreground hover:text-destructive"
                                aria-label={`Remove ${label} hook ${i + 1}`}
                                title="Remove hook"
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                    );
                })
            )}
            <Button type="button" variant="outline" size="sm" onClick={add}>
                <Plus className="mr-1 h-3 w-3" /> Add {label} hook
            </Button>
        </div>
    );
}

function stringify(v) {
    try {
        return JSON.stringify(v, null, 2);
    } catch {
        return '';
    }
}
function tryParse(s) {
    try {
        const v = JSON.parse(s);
        return { ok: true, value: v };
    } catch {
        return { ok: false };
    }
}
